export type BehaviorSlug =
  | "practices-self-awareness"
  | "diagnoses-needs"
  | "innovates"
  | "builds-trust"
  | "champions-change"
  | "focuses-on-results-through-people"
  | "sets-goals"
  | "pursues-self-development"
  | "takes-initiative"
  | "solves-problems"
  | "matches-style-to-needs"
  | "leverages-diversity"
  | "demonstrates-self-management"
  | "inspires"
  | "communicates"
  | "collaborates-and-promotes-teamwork";

export interface Behavior {
  slug: BehaviorSlug;
  number: number;
  name: string;
  statement: string;
  /** 360 wording: the leader rating themselves. */
  selfStatement: string;
  /** 360 wording: a direct report rating their manager. */
  directReportStatement: string;
  /** 360 wording: a peer or the leader's own manager rating them. */
  observerStatement: string;
  definition: string;
  dimension: DimensionSlug;
  curatedCourses: { title: string; url: string }[];
  resultIntro: string;
}

export type DimensionSlug =
  | "leads-self"
  | "understands-and-adapts"
  | "delivers-results"
  | "mobilizes-people";

export interface Dimension {
  slug: DimensionSlug;
  name: string;
  description: string;
  heading: string;
  interpretation: string;
  actions: string[];
}

export const dimensions: Record<DimensionSlug, Dimension> = {
  "leads-self": {
    slug: "leads-self",
    name: "Leads Self",
    description:
      "Leaders understand and manage themselves, take ownership, and continue developing their capability.",
    heading: "Strengthen the foundation leaders bring to every situation",
    interpretation:
      "Leaders may be acting without enough reflection, reacting under pressure, waiting for direction, or treating development as something the organization owns for them.",
    actions: [
      "Introduce a shared self-awareness framework and structured reflection.",
      "Build feedback and individual development goals into the leadership rhythm.",
      "Give leaders real opportunities to practice initiative and self-management with coaching support.",
    ],
  },
  "understands-and-adapts": {
    slug: "understands-and-adapts",
    name: "Understands and Adapts",
    description:
      "Leaders understand people and situations, incorporate different perspectives, and adjust how they communicate and lead.",
    heading: "Help leaders understand before they act",
    interpretation:
      "Leaders may rely on a single communication or leadership style, overlook different perspectives, or respond to symptoms without understanding the underlying need.",
    actions: [
      "Teach leaders to diagnose the person, task, and situation before choosing an approach.",
      "Strengthen listening and two-way communication practices.",
      "Use different perspectives and working styles intentionally in decisions and problem-solving.",
    ],
  },
  "delivers-results": {
    slug: "delivers-results",
    name: "Delivers Results",
    description:
      "Leaders turn priorities into goals, improve how work gets done, solve meaningful problems, and achieve results through others.",
    heading: "Connect people leadership more clearly to execution",
    interpretation:
      "Priorities may remain vague, leaders may solve the same problems repeatedly, or results may depend on personal effort rather than clear goals and capable teams.",
    actions: [
      "Translate business priorities into clear team goals and measures.",
      "Establish a practical root-cause problem-solving approach.",
      "Help managers improve results by developing capability and ownership in others.",
    ],
  },
  "mobilizes-people": {
    slug: "mobilizes-people",
    name: "Mobilizes People",
    description:
      "Leaders create the trust, meaning, collaboration, and commitment people need to move forward together.",
    heading: "Build the trust and commitment that move people forward",
    interpretation:
      "Employees may understand what to do without feeling connected to one another, confident in leadership, or committed to change.",
    actions: [
      "Define the consistent leadership actions that build or weaken trust.",
      "Equip leaders to explain change, address concerns, and reinforce adoption.",
      "Create shared goals and routines that require collaboration across roles or teams.",
    ],
  },
};

export const behaviors: Behavior[] = [
  {
    slug: "practices-self-awareness",
    number: 1,
    name: "Practices Self-Awareness",
    statement:
      "Our leaders recognize how their own strengths, habits, emotions, and impact influence the people around them.",
    selfStatement:
      "I recognize how my own strengths, habits, emotions, and impact influence the people around me.",
    directReportStatement:
      "My manager recognizes how their own strengths, habits, emotions, and impact influence the people around them.",
    observerStatement:
      "This leader recognizes how their own strengths, habits, emotions, and impact influence the people around them.",
    definition:
      "Understands personal patterns and seeks an accurate view of how one's behavior affects others.",
    dimension: "leads-self",
    resultIntro:
      "Strengthen leaders' understanding of their patterns, reactions, communication preferences, and impact on others.",
    curatedCourses: [
      {
        title: "Leadership Essentials with a Foundation of Emotional Intelligence",
        url: "/course-catalog/leadership-essentials-with-a-foundation-of-emotional-intelligence/",
      },
      { title: "Emotional Triggers", url: "/course-catalog/emotional-triggers/" },
      {
        title: "Improve Communication Through the 4P Personality Styles",
        url: "/course-catalog/improve-communication-through-the-4p-personality-styles/",
      },
    ],
  },
  {
    slug: "diagnoses-needs",
    number: 2,
    name: "Diagnoses Needs",
    statement:
      "Our leaders look beyond the first symptom to understand what a person, team, or situation actually needs.",
    selfStatement:
      "I look beyond the first symptom to understand what a person, team, or situation actually needs.",
    directReportStatement:
      "My manager looks beyond the first symptom to understand what a person, team, or situation actually needs.",
    observerStatement:
      "This leader looks beyond the first symptom to understand what a person, team, or situation actually needs.",
    definition:
      "Gathers relevant information, listens, and distinguishes the underlying need from the presenting problem.",
    dimension: "understands-and-adapts",
    resultIntro:
      "Help leaders gather relevant information, look beyond first symptoms, and determine what the person or situation actually needs.",
    curatedCourses: [
      { title: "Analytical Thinking Skills", url: "/course-catalog/analytical-thinking-skills/" },
      {
        title: "Making Better Decisions through Critical Thinking",
        url: "/course-catalog/making-better-decisions-through-critical-thinking/",
      },
      { title: "Creative Problem Solving", url: "/course-catalog/creative-problem-solving/" },
    ],
  },
  {
    slug: "innovates",
    number: 3,
    name: "Innovates",
    statement:
      "Our leaders encourage useful new ideas and are willing to improve established ways of working.",
    selfStatement:
      "I encourage useful new ideas and am willing to improve established ways of working.",
    directReportStatement:
      "My manager encourages useful new ideas and is willing to improve established ways of working.",
    observerStatement:
      "This leader encourages useful new ideas and is willing to improve established ways of working.",
    definition:
      "Questions assumptions, explores possibilities, and supports practical experimentation that creates value.",
    dimension: "delivers-results",
    resultIntro:
      "Give leaders practical ways to explore improvements, move useful ideas forward, and convert possibilities into action.",
    curatedCourses: [
      {
        title: "Turn Your Ideas into Results",
        url: "/course-catalog/turn-your-ideas-into-results/",
      },
      { title: "SOAR Analysis", url: "/course-catalog/soar-analysis/" },
      {
        title: "Practical Project Management",
        url: "/course-catalog/practical-project-management/",
      },
    ],
  },
  {
    slug: "builds-trust",
    number: 4,
    name: "Builds Trust",
    statement:
      "Our leaders act with consistency, honesty, and respect in ways that earn employee confidence.",
    selfStatement:
      "I act with consistency, honesty, and respect in ways that earn people's confidence.",
    directReportStatement:
      "My manager acts with consistency, honesty, and respect in ways that earn my confidence.",
    observerStatement:
      "This leader acts with consistency, honesty, and respect in ways that earn people's confidence.",
    definition:
      "Follows through on commitments, communicates candidly, and creates confidence through reliable behavior.",
    dimension: "mobilizes-people",
    resultIntro:
      "Develop the consistency, openness, follow-through, and relationship practices that earn employee confidence.",
    curatedCourses: [
      { title: "Creating a Culture of Trust", url: "/course-catalog/creating-a-culture-of-trust/" },
      { title: "Psychological Safety", url: "/course-catalog/psychological-safety/" },
      {
        title: "Building Authentic Relationships",
        url: "/course-catalog/building-authentic-relationships/",
      },
    ],
  },
  {
    slug: "champions-change",
    number: 5,
    name: "Champions Change",
    statement: "Our leaders help employees understand, navigate, and commit to necessary change.",
    selfStatement: "I help people understand, navigate, and commit to necessary change.",
    directReportStatement:
      "My manager helps people understand, navigate, and commit to necessary change.",
    observerStatement:
      "This leader helps people understand, navigate, and commit to necessary change.",
    definition:
      "Explains the reason for change, acknowledges concerns, models adaptability, and reinforces forward movement.",
    dimension: "mobilizes-people",
    resultIntro:
      "Prepare leaders to explain change, address concerns, model adaptability, and reinforce forward movement.",
    curatedCourses: [
      { title: "Leading Change", url: "/course-catalog/leading-change/" },
      {
        title: "Turn Your Ideas into Results",
        url: "/course-catalog/turn-your-ideas-into-results/",
      },
      {
        title: "The Experienced Leader Boot Camp",
        url: "/course-catalog/the-experienced-leader-boot-camp/",
      },
    ],
  },
  {
    slug: "focuses-on-results-through-people",
    number: 6,
    name: "Focuses on Results through People",
    statement:
      "Our leaders achieve results by setting people up to succeed, not simply by pushing harder or taking over the work themselves.",
    selfStatement:
      "I achieve results by setting people up to succeed, not by pushing harder or taking over the work myself.",
    directReportStatement:
      "My manager achieves results by setting people up to succeed, not by pushing harder or taking over the work themselves.",
    observerStatement:
      "This leader achieves results by setting people up to succeed, not by pushing harder or taking over the work themselves.",
    definition:
      "Connects performance expectations with clarity, support, accountability, and employee capability.",
    dimension: "delivers-results",
    resultIntro:
      "Help managers achieve results by building clarity, ownership, capability, and accountability in others.",
    curatedCourses: [
      { title: "Delegating for Development", url: "/course-catalog/delegating-for-development/" },
      {
        title: "Development to Delegation Cycle",
        url: "/course-catalog/development-to-delegation-cycle/",
      },
      { title: "Performance Management", url: "/course-catalog/performance-management/" },
    ],
  },
  {
    slug: "sets-goals",
    number: 7,
    name: "Sets Goals",
    statement:
      "Our leaders translate priorities into clear, meaningful goals with visible measures of success.",
    selfStatement:
      "I translate priorities into clear, meaningful goals with visible measures of success.",
    directReportStatement:
      "My manager translates priorities into clear, meaningful goals with visible measures of success.",
    observerStatement:
      "This leader translates priorities into clear, meaningful goals with visible measures of success.",
    definition:
      "Establishes specific outcomes, aligns goals with business needs, and clarifies how progress will be evaluated.",
    dimension: "delivers-results",
    resultIntro:
      "Equip leaders to turn priorities into specific outcomes, ownership, measures, and review points.",
    curatedCourses: [
      {
        title: "Driving Results through Goal Setting",
        url: "/course-catalog/driving-results-through-goal-setting/",
      },
      {
        title: "Fundamentals of Strategic Planning",
        url: "/course-catalog/fundamentals-of-strategic-planning/",
      },
      {
        title: "Practical Project Management",
        url: "/course-catalog/practical-project-management/",
      },
    ],
  },
  {
    slug: "pursues-self-development",
    number: 8,
    name: "Pursues Self-Development",
    statement:
      "Our leaders actively seek feedback, learning, and experiences that strengthen how they lead.",
    selfStatement:
      "I actively seek feedback, learning, and experiences that strengthen how I lead.",
    directReportStatement:
      "My manager actively seeks feedback, learning, and experiences that strengthen how they lead.",
    observerStatement:
      "This leader actively seeks feedback, learning, and experiences that strengthen how they lead.",
    definition:
      "Takes ownership of continued growth and applies new insight to current leadership responsibilities.",
    dimension: "leads-self",
    resultIntro:
      "Encourage leaders to take ownership of continued growth and apply new insight to their current responsibilities.",
    curatedCourses: [
      { title: "Developing a Growth Mindset", url: "/course-catalog/developing-a-growth-mindset/" },
      { title: "Maximizing Strengths", url: "/course-catalog/maximizing-strengths/" },
      {
        title: "Professional Pathways: Building a Vision for Your Career",
        url: "/course-catalog/professional-pathways-building-a-vision-for-your-career/",
      },
    ],
  },
  {
    slug: "takes-initiative",
    number: 9,
    name: "Takes Initiative",
    statement:
      "Our leaders act on important needs and opportunities without waiting for every step to be directed.",
    selfStatement:
      "I act on important needs and opportunities without waiting for every step to be directed.",
    directReportStatement:
      "My manager acts on important needs and opportunities without waiting for every step to be directed.",
    observerStatement:
      "This leader acts on important needs and opportunities without waiting for every step to be directed.",
    definition:
      "Demonstrates appropriate ownership, anticipates what is needed, and moves useful work forward.",
    dimension: "leads-self",
    resultIntro:
      "Help leaders recognize important needs, take appropriate ownership, and move useful work forward.",
    curatedCourses: [
      {
        title: "How to Make Yourself Indispensable",
        url: "/course-catalog/how-to-make-yourself-indispensable/",
      },
      { title: "True Empowerment", url: "/course-catalog/true-empowerment/" },
      { title: "SOAR Analysis", url: "/course-catalog/soar-analysis/" },
    ],
  },
  {
    slug: "solves-problems",
    number: 10,
    name: "Solves Problems",
    statement:
      "Our leaders address root causes, involve the right people, and make practical decisions that hold up over time.",
    selfStatement:
      "I address root causes, involve the right people, and make practical decisions that hold up over time.",
    directReportStatement:
      "My manager addresses root causes, involves the right people, and makes practical decisions that hold up over time.",
    observerStatement:
      "This leader addresses root causes, involves the right people, and makes practical decisions that hold up over time.",
    definition:
      "Defines problems accurately, evaluates options, and follows through on solutions rather than repeatedly treating symptoms.",
    dimension: "delivers-results",
    resultIntro:
      "Strengthen how leaders define problems, find root causes, evaluate options, and follow through on durable solutions.",
    curatedCourses: [
      { title: "Creative Problem Solving", url: "/course-catalog/creative-problem-solving/" },
      {
        title: "Making Better Decisions through Critical Thinking",
        url: "/course-catalog/making-better-decisions-through-critical-thinking/",
      },
      { title: "Analytical Thinking Skills", url: "/course-catalog/analytical-thinking-skills/" },
    ],
  },
  {
    slug: "matches-style-to-needs",
    number: 11,
    name: "Matches Style to Needs",
    statement:
      "Our leaders adapt how they communicate, support, and direct others based on the person and situation.",
    selfStatement:
      "I adapt how I communicate, support, and direct others based on the person and situation.",
    directReportStatement:
      "My manager adapts how they communicate, support, and direct others based on the person and situation.",
    observerStatement:
      "This leader adapts how they communicate, support, and direct others based on the person and situation.",
    definition:
      "Adjusts leadership approach without lowering standards or becoming inconsistent in expectations.",
    dimension: "understands-and-adapts",
    resultIntro:
      "Help leaders adapt their direction, support, and communication to the person and situation without lowering expectations.",
    curatedCourses: [
      {
        title: "Adapting Your Leadership Style",
        url: "/course-catalog/adapting-your-leadership-style/",
      },
      {
        title: "Improve Communication Through the 4P Personality Styles",
        url: "/course-catalog/improve-communication-through-the-4p-personality-styles/",
      },
      {
        title: "Motivation Models: Carrot or Stick?",
        url: "/course-catalog/motivation-models-carrot-or-stick/",
      },
    ],
  },
  {
    slug: "leverages-diversity",
    number: 12,
    name: "Leverages Diversity",
    statement:
      "Our leaders seek and use different perspectives, experiences, and strengths to improve decisions and results.",
    selfStatement:
      "I seek and use different perspectives, experiences, and strengths to improve decisions and results.",
    directReportStatement:
      "My manager seeks and uses different perspectives, experiences, and strengths to improve decisions and results.",
    observerStatement:
      "This leader seeks and uses different perspectives, experiences, and strengths to improve decisions and results.",
    definition:
      "Creates meaningful opportunities for varied viewpoints to shape thinking, problem-solving, and execution.",
    dimension: "understands-and-adapts",
    resultIntro:
      "Prepare leaders to seek, understand, and use different perspectives, experiences, and strengths.",
    curatedCourses: [
      { title: "Connecting Across Cultures", url: "/course-catalog/connecting-across-cultures/" },
      {
        title: "Leading with an Inclusive Mindset",
        url: "/course-catalog/leading-with-an-inclusive-mindset/",
      },
      {
        title: "Equity, Diversity, and Inclusion in the Workplace",
        url: "/course-catalog/equity-diversity-and-inclusion-in-the-workplace/",
      },
    ],
  },
  {
    slug: "demonstrates-self-management",
    number: 13,
    name: "Demonstrates Self-Management",
    statement:
      "Our leaders remain constructive and intentional when pressure, conflict, or strong emotions are present.",
    selfStatement:
      "I remain constructive and intentional when pressure, conflict, or strong emotions are present.",
    directReportStatement:
      "My manager remains constructive and intentional when pressure, conflict, or strong emotions are present.",
    observerStatement:
      "This leader remains constructive and intentional when pressure, conflict, or strong emotions are present.",
    definition:
      "Regulates reactions, follows through on priorities, and chooses behavior that supports the situation's needs.",
    dimension: "leads-self",
    resultIntro:
      "Help leaders remain constructive and intentional when pressure, conflict, competing demands, or strong emotions are present.",
    curatedCourses: [
      { title: "Emotional Triggers", url: "/course-catalog/emotional-triggers/" },
      { title: "Managing Stress", url: "/course-catalog/managing-stress/" },
      {
        title: "Mindfulness as a Differentiator",
        url: "/course-catalog/mindfulness-as-a-differentiator/",
      },
    ],
  },
  {
    slug: "inspires",
    number: 14,
    name: "Inspires",
    statement:
      "Our leaders help people see why their work matters and believe their contribution can make a difference.",
    selfStatement:
      "I help people see why their work matters and believe their contribution can make a difference.",
    directReportStatement:
      "My manager helps people see why their work matters and believe their contribution can make a difference.",
    observerStatement:
      "This leader helps people see why their work matters and believe their contribution can make a difference.",
    definition:
      "Connects work to purpose, expresses meaningful confidence, and creates energy around worthwhile goals.",
    dimension: "mobilizes-people",
    resultIntro:
      "Help leaders connect work to purpose, express meaningful confidence, and create energy around worthwhile goals.",
    curatedCourses: [
      {
        title: "Introduction to Servant Leadership",
        url: "/course-catalog/introduction-to-servant-leadership/",
      },
      {
        title: "Developing Others: Your Top Priority",
        url: "/course-catalog/developing-others-your-top-priority/",
      },
      { title: "Powerful Presentations", url: "/course-catalog/powerful-presentations/" },
    ],
  },
  {
    slug: "communicates",
    number: 15,
    name: "Communicates",
    statement:
      "Our leaders communicate expectations, decisions, and feedback clearly, and listen to understand.",
    selfStatement:
      "I communicate expectations, decisions, and feedback clearly, and listen to understand.",
    directReportStatement:
      "My manager communicates expectations, decisions, and feedback clearly, and listens to understand.",
    observerStatement:
      "This leader communicates expectations, decisions, and feedback clearly, and listens to understand.",
    definition: "Exchanges information in a timely, direct, respectful, and two-way manner.",
    dimension: "understands-and-adapts",
    resultIntro:
      "Strengthen how leaders clarify expectations and decisions, provide feedback, and listen to understand.",
    curatedCourses: [
      {
        title: "Improve Communication Through the 4P Personality Styles",
        url: "/course-catalog/improve-communication-through-the-4p-personality-styles/",
      },
      {
        title: "Strengthening Teams through Communication",
        url: "/course-catalog/strengthening-teams-through-communication/",
      },
      { title: "Assertiveness Skills", url: "/course-catalog/assertiveness-skills/" },
    ],
  },
  {
    slug: "collaborates-and-promotes-teamwork",
    number: 16,
    name: "Collaborates and Promotes Teamwork",
    statement:
      "Our leaders bring people together across roles and boundaries to solve problems and achieve shared outcomes.",
    selfStatement:
      "I bring people together across roles and boundaries to solve problems and achieve shared outcomes.",
    directReportStatement:
      "My manager brings people together across roles and boundaries to solve problems and achieve shared outcomes.",
    observerStatement:
      "This leader brings people together across roles and boundaries to solve problems and achieve shared outcomes.",
    definition:
      "Reduces silos, supports productive disagreement, and reinforces shared ownership for results.",
    dimension: "mobilizes-people",
    resultIntro:
      "Help leaders reduce silos, support productive teamwork, and reinforce shared ownership for results.",
    curatedCourses: [
      { title: "Skillful Collaboration", url: "/course-catalog/skillful-collaboration/" },
      { title: "Developing Dynamic Teams", url: "/course-catalog/developing-dynamic-teams/" },
      {
        title: "Strengthening Teams through Communication",
        url: "/course-catalog/strengthening-teams-through-communication/",
      },
    ],
  },
];

export const dimensionList: DimensionSlug[] = [
  "leads-self",
  "understands-and-adapts",
  "delivers-results",
  "mobilizes-people",
];
